export type DateLike = Date | string | null | undefined;

function isValidDate(d: Date): boolean {
  return !Number.isNaN(d.getTime());
}

/**
 * Parse a value into a Date normalized to local midnight (00:00:00.000),
 * comparing ONLY the calendar date in the user's timezone.
 *
 * Important: `YYYY-MM-DD` is treated as a local date (not UTC) to avoid
 * timezone shifts (e.g. UTC-03 showing the previous day).
 */
export function toLocalMidnightDate(value: DateLike): Date | null {
  if (value == null) return null;

  if (value instanceof Date) {
    if (!isValidDate(value)) return null;
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;

    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
      const year = Number(m[1]);
      const monthIndex = Number(m[2]) - 1;
      const day = Number(m[3]);
      const d = new Date(year, monthIndex, day, 0, 0, 0, 0);
      return isValidDate(d) ? d : null;
    }

    const parsed = new Date(s);
    if (!isValidDate(parsed)) return null;
    const d = new Date(parsed);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  return null;
}

export function isLocalDateAfter(a: DateLike, b: DateLike): boolean | null {
  const da = toLocalMidnightDate(a);
  const db = toLocalMidnightDate(b);
  if (!da || !db) return null;
  return da.getTime() > db.getTime();
}

export function minLocalMidnightDate(values: DateLike[]): Date | null {
  let min: Date | null = null;

  for (const v of values) {
    const d = toLocalMidnightDate(v);
    if (!d) continue;
    if (!min || d.getTime() < min.getTime()) {
      min = d;
    }
  }

  return min;
}


/** Format a Date as local 'yyyy-MM-dd' (no timezone shift). */
export function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Add `days` to a local 'yyyy-MM-dd' date and return the result in the same format. */
export function addDays(ymd: string, days: number): string {
  const d = new Date(ymd + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return formatLocalYmd(d);
}
